import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

// 커서 위치는 초당 수십 번 갱신되므로 별도 컨텍스트로 분리 —
// CursorOverlay만 리렌더되고 나머지 앱은 영향받지 않음
const CursorContext = createContext({});
export const useCursors = () => useContext(CursorContext);

// 접속 세션별 표시 이름 — 같은 유저가 여러 브라우저로 접속하면 "이름 (1)", "이름 (2)"
// 반환: { [socket_id]: label }
export const useSessionLabels = () => {
  const ctx = useContext(SocketContext);
  const connectedUsers = ctx?.connectedUsers;
  return useMemo(() => {
    const counts = {};
    const seen = {};
    const map = {};
    (connectedUsers || []).forEach(u => { counts[u.user_id] = (counts[u.user_id] || 0) + 1; });
    (connectedUsers || []).forEach(u => {
      seen[u.user_id] = (seen[u.user_id] || 0) + 1;
      if (u.socket_id) {
        map[u.socket_id] = counts[u.user_id] > 1 ? `${u.user_name} (${seen[u.user_id]})` : u.user_name;
      }
    });
    return map;
  }, [connectedUsers]);
};

// 커서 쓰로틀 간격 (ms)
const CURSOR_THROTTLE = 50;

export const SocketProvider = ({ children }) => {
  const { isLoggedIn, user } = useAuth();
  const socketRef = useRef(null);
  const cursorTimerRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [mySocketId, setMySocketId] = useState(null);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [activeSelections, setActiveSelections] = useState({});
  const [currentChartId, setCurrentChartId] = useState(null);
  const [chartVersion, setChartVersion] = useState(1);

  // Socket 연결
  useEffect(() => {
    if (isLoggedIn && !socketRef.current) {
      const socket = io(window.location.origin, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        path: '/socket.io',
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      socket.on('connect', () => {
        console.log('[Socket] Connected:', socket.id);
        setConnected(true);
        setMySocketId(socket.id);

        // 재연결 시 이전 차트에 자동 재참가
        if (socket._chartId) {
          console.log('[Socket] Rejoining chart:', socket._chartId);
          socket.emit('chart:join', { chartId: socket._chartId });
        }
      });

      socket.on('connect_error', (err) => {
        console.error('[Socket] connect_error:', err.message);
      });

      socket.on('disconnect', () => {
        console.log('[Socket] Disconnected');
        setConnected(false);
        setConnectedUsers([]);
        setRemoteCursors({});
        setActiveSelections({});
      });

      // 차트 참가 완료
      socket.on('chart:joined', ({ users, chartVersion: ver }) => {
        setConnectedUsers(users);
        setChartVersion(ver);
      });

      // 유저 참가 — 접속(소켓) 단위로 추가. 같은 유저 다중 브라우저도 각각 표시
      socket.on('chart:user-joined', (userData) => {
        setConnectedUsers(prev => {
          if (userData.socketId && prev.find(u => u.socket_id === userData.socketId)) return prev;
          return [...prev, {
            socket_id: userData.socketId,
            user_id: userData.userId,
            user_name: userData.userName,
            avatar_url: userData.avatarUrl,
            cursor_color: userData.cursorColor,
          }];
        });
      });

      // 유저 퇴장 — 해당 접속(소켓)만 제거. 같은 유저의 다른 브라우저는 유지
      socket.on('chart:user-left', ({ socketId: sid, userId }) => {
        setConnectedUsers(prev => prev.filter(u => (sid ? u.socket_id !== sid : u.user_id !== userId)));
        setRemoteCursors(prev => {
          const next = {};
          for (const [key, cursor] of Object.entries(prev)) {
            if (sid ? key !== sid : (cursor.userId !== userId && key !== userId)) next[key] = cursor;
          }
          return next;
        });
        setActiveSelections(prev => {
          const next = { ...prev };
          delete next[userId];
          return next;
        });
      });

      // 커서 이동 (소켓 단위 키 — 같은 유저 멀티탭도 각자 커서 유지)
      socket.on('cursor:moved', (data) => {
        if (!data) return;
        const key = data.socketId || data.userId;
        setRemoteCursors(prev => ({
          ...prev,
          [key]: { ...data, timestamp: Date.now() }
        }));
      });

      // 선택 변경
      socket.on('selection:active', ({ userId, userName, cursorColor, taskId }) => {
        if (taskId === null) {
          setActiveSelections(prev => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
        } else {
          setActiveSelections(prev => ({
            ...prev,
            [userId]: { userName, cursorColor, taskId }
          }));
        }
      });

      // 버전 업데이트
      socket.on('version:updated', ({ version }) => {
        setChartVersion(version);
      });

      // 에러
      socket.on('error', ({ message }) => {
        console.error('[Socket] Error:', message);
      });

      socketRef.current = socket;
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
    };
  }, [isLoggedIn]);

  // 차트 참가
  const joinChart = useCallback((chartId) => {
    if (!socketRef.current || !chartId) return;
    setCurrentChartId(chartId);
    socketRef.current._chartId = chartId; // 재연결용 저장
    socketRef.current.emit('chart:join', { chartId });
  }, []);

  // 차트 나가기
  const leaveChart = useCallback(() => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('chart:leave');
    socketRef.current._chartId = null; // 재연결 방지
    setCurrentChartId(null);
    setConnectedUsers([]);
    setRemoteCursors({});
    setActiveSelections({});
  }, [currentChartId]);

  // 커서 이동 (쓰로틀)
  // payload: { day, y, view } — day는 UTC 기준 일수(소수), 뷰마다 자신의 스케일로 환산
  const emitCursorMove = useCallback((payload) => {
    if (!socketRef.current || !currentChartId) return;
    if (cursorTimerRef.current) return;
    cursorTimerRef.current = setTimeout(() => {
      cursorTimerRef.current = null;
    }, CURSOR_THROTTLE);
    socketRef.current.emit('cursor:move', payload);
  }, [currentChartId]);

  // 선택 알림
  const emitSelection = useCallback((taskId) => {
    if (!socketRef.current || !currentChartId) return;
    if (taskId) {
      socketRef.current.emit('selection:task', { taskId });
    } else {
      socketRef.current.emit('selection:cleared');
    }
  }, [currentChartId]);

  // Task 이벤트
  const emitTaskAdd = useCallback((task) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('task:add', { chartId: currentChartId, task, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitTaskUpdate = useCallback((taskId, changes) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('task:update', { chartId: currentChartId, taskId, changes, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitTaskDelete = useCallback((taskId) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('task:delete', { chartId: currentChartId, taskId, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitTaskReorder = useCallback((taskIds) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('task:reorder', { chartId: currentChartId, taskIds, version: chartVersion });
  }, [currentChartId, chartVersion]);

  // Milestone 이벤트
  const emitMilestoneAdd = useCallback((milestone) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('milestone:add', { chartId: currentChartId, milestone, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitMilestoneUpdate = useCallback((milestoneId, changes) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('milestone:update', { chartId: currentChartId, milestoneId, changes, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitMilestoneDelete = useCallback((milestoneId) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('milestone:delete', { chartId: currentChartId, milestoneId, version: chartVersion });
  }, [currentChartId, chartVersion]);

  // Member 이벤트
  const emitMemberAdd = useCallback((member) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('member:add', { chartId: currentChartId, member, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitMemberUpdate = useCallback((memberId, changes) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('member:update', { chartId: currentChartId, memberId, changes, version: chartVersion });
  }, [currentChartId, chartVersion]);

  const emitMemberRemove = useCallback((memberId) => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('member:remove', { chartId: currentChartId, memberId, version: chartVersion });
  }, [currentChartId, chartVersion]);

  // 동기화 요청
  const requestSync = useCallback(() => {
    if (!socketRef.current || !currentChartId) return;
    socketRef.current.emit('sync:request', { chartId: currentChartId });
  }, [currentChartId]);

  // 커서(remoteCursors)는 별도 컨텍스트 — 메인 컨텍스트는 커서 이동에 영향받지 않음
  const value = useMemo(() => ({
    socket: socketRef.current,
    connected,
    socketId: mySocketId,
    connectedUsers,
    activeSelections,
    currentChartId,
    chartVersion,
    setChartVersion,
    joinChart,
    leaveChart,
    emitCursorMove,
    emitSelection,
    emitTaskAdd,
    emitTaskUpdate,
    emitTaskDelete,
    emitTaskReorder,
    emitMilestoneAdd,
    emitMilestoneUpdate,
    emitMilestoneDelete,
    emitMemberAdd,
    emitMemberUpdate,
    emitMemberRemove,
    requestSync,
  }), [
    connected, mySocketId, connectedUsers, activeSelections, currentChartId, chartVersion,
    joinChart, leaveChart, emitCursorMove, emitSelection,
    emitTaskAdd, emitTaskUpdate, emitTaskDelete, emitTaskReorder,
    emitMilestoneAdd, emitMilestoneUpdate, emitMilestoneDelete,
    emitMemberAdd, emitMemberUpdate, emitMemberRemove, requestSync,
  ]);

  return (
    <SocketContext.Provider value={value}>
      <CursorContext.Provider value={remoteCursors}>
        {children}
      </CursorContext.Provider>
    </SocketContext.Provider>
  );
};
